import axios from 'axios';

// Crear instancia de axios
const instance = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL || '',
  headers: {
    'Content-Type': 'application/json'
  }
});

// Variable para controlar si estamos renovando el token
let isRefreshing = false;
// Cola de solicitudes pendientes
let failedQueue = [];

// Función para procesar la cola de solicitudes
const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  
  failedQueue = [];
};

// Función para renovar el token
const refreshToken = async () => {
  try {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) return false;
    
    const response = await axios.post(
      `${process.env.REACT_APP_BACKEND_URL || ''}/api/auth/refresh-token`, 
      { refreshToken }
    );
    
    if (response.data.accessToken) {
      localStorage.setItem('token', response.data.accessToken);
      return true;
    }
    return false;
  } catch (error) {
    console.error('Error al renovar token:', error);
    // Limpiar tokens en caso de error
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    return false;
  }
};

// Interceptor para añadir el token a las solicitudes
instance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers = {
        ...config.headers,
        'Authorization': `Bearer ${token}`
      };
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para manejar errores de respuesta
instance.interceptors.response.use(
  (response) => {
    // Verificar si el token está por expirar
    if (response.headers['x-token-expiring-soon']) {
      // Renovar token en segundo plano
      refreshToken().catch(console.error);
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    
    // Si el error es 401 (Unauthorized) y no hemos intentado renovar el token
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Si es un error de token expirado y tenemos un refreshToken
      if (localStorage.getItem('refreshToken')) {
        if (isRefreshing) {
          // Si ya estamos renovando, añadir a la cola
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then(token => {
              originalRequest.headers['Authorization'] = `Bearer ${token}`;
              return instance(originalRequest);
            })
            .catch(err => {
              return Promise.reject(err);
            });
        }
        
        originalRequest._retry = true;
        isRefreshing = true;
        
        try {
          // Intentar renovar el token
          const success = await refreshToken();
          if (success) {
            const newToken = localStorage.getItem('token');
            processQueue(null, newToken);
            originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
            return instance(originalRequest);
          } else {
            processQueue(new Error('Failed to refresh token'));
            // Redirigir al login
            localStorage.removeItem('token');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('user');
            window.location.href = '/login';
            return Promise.reject(error);
          }
        } catch (refreshError) {
          processQueue(refreshError);
          // Redirigir al login
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('user');
          window.location.href = '/login';
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      } else {
        // No tenemos refreshToken, limpiar y redirigir al login
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
    }
    
    return Promise.reject(error);
  }
);

export default instance;